import { supabase } from './supabase'
import { addTrackingEvent, addOrderLog } from './fulfillment'

// ─── TYPES ────────────────────────────────────────────────────────

export interface AvailableDriver {
  driver_id: number
  driver_name: string
  lat: number | null
  lng: number | null
}

export interface DispatchResult {
  success: boolean
  offersCreated: number
  driversFound: number
  error?: string
}

// ─── CONFIG ───────────────────────────────────────────────────────

const OFFER_EXPIRY_SECONDS = 300 // 5 minutes per offer

// ─── FIND AVAILABLE DRIVERS ───────────────────────────────────────

/**
 * Finds drivers eligible to receive a delivery offer.
 *
 * Eligibility:
 * 1. drivers.is_active = true
 * 2. driver_users link is active (driver_users.is_active = true)
 * 3. driver_sessions.is_online = true
 * 4. No current active trip (delivery_jobs with status in assigned/arrived_pickup/picked_up/en_route)
 *
 * Optional: filter by restaurant_id scope if driver_users has restaurant_id.
 */
export async function findAvailableDrivers(
  restaurantId: string,
): Promise<AvailableDriver[]> {
  try {
    console.log(
      '[DISPATCH] Finding available drivers for restaurant:',
      restaurantId,
    )

    // Step 1: Get online driver session IDs
    const { data: sessions, error: sessionsError } = await supabase
      .from('driver_sessions')
      .select('driver_id, lat, lng')
      .eq('is_online', true)

    if (sessionsError || !sessions || sessions.length === 0) {
      console.log('[DISPATCH] No online driver sessions found:', sessionsError)
      return []
    }

    const onlineDriverIds = sessions.map((s) => s.driver_id)
    console.log('[DISPATCH] Online driver IDs:', onlineDriverIds)

    // Step 2: Filter to active drivers with active driver_users link
    const { data: activeLinks, error: linksError } = await supabase
      .from('driver_users')
      .select('driver_id')
      .in('driver_id', onlineDriverIds)
      .eq('is_active', true)

    if (linksError || !activeLinks || activeLinks.length === 0) {
      console.log('[DISPATCH] No active driver_users links found:', linksError)
      return []
    }

    const linkedDriverIds = activeLinks.map((l) => l.driver_id)
    console.log('[DISPATCH] Linked active driver IDs:', linkedDriverIds)

    // Step 3: Filter to active drivers
    const { data: activeDrivers, error: driversError } = await supabase
      .from('drivers')
      .select('id, full_name')
      .in('id', linkedDriverIds)
      .eq('is_active', true)

    if (driversError || !activeDrivers || activeDrivers.length === 0) {
      console.log('[DISPATCH] No active drivers found:', driversError)
      return []
    }

    const activeDriverIds = activeDrivers.map((d) => d.id)
    console.log('[DISPATCH] Active driver IDs:', activeDriverIds)

    // Step 4: Exclude drivers with an active trip
    const { data: busyJobs, error: busyError } = await supabase
      .from('delivery_jobs')
      .select('driver_id')
      .in('driver_id', activeDriverIds)
      .in('status', ['assigned', 'arrived_pickup', 'picked_up', 'en_route'])

    const busyDriverIds = new Set(
      (busyJobs || []).map((j) => j.driver_id).filter(Boolean),
    )

    if (busyError) {
      console.warn('[DISPATCH] Error checking busy drivers:', busyError)
      // Continue anyway — better to offer than to block
    }

    console.log('[DISPATCH] Busy driver IDs:', [...busyDriverIds])

    // Build final list
    const sessionMap = new Map(sessions.map((s) => [s.driver_id, s]))
    const driverMap = new Map(activeDrivers.map((d) => [d.id, d]))

    const available: AvailableDriver[] = activeDriverIds
      .filter((id) => !busyDriverIds.has(id))
      .map((id) => {
        const session = sessionMap.get(id)
        const driver = driverMap.get(id)
        return {
          driver_id: id,
          driver_name: driver?.full_name || 'Unknown',
          lat: session?.lat || null,
          lng: session?.lng || null,
        }
      })

    console.log(`[DISPATCH] ✅ Found ${available.length} available drivers`)
    return available
  } catch (err) {
    console.error('[DISPATCH] Exception finding drivers:', err)
    return []
  }
}

// ─── DISPATCH DELIVERY JOB ────────────────────────────────────────

/**
 * Creates delivery_job_offers for all available drivers.
 * Called immediately after a delivery_job is created.
 *
 * Safety:
 * - Checks for existing pending offers to prevent duplicates
 * - Each offer has an expiry timestamp
 * - Logs all actions to order_logs
 */
export async function dispatchDeliveryJob(
  deliveryJobId: number,
  restaurantId: string,
  orderId: string,
): Promise<DispatchResult> {
  try {
    console.log('[DISPATCH] ═══════════════════════════════════')
    console.log('[DISPATCH] Dispatching delivery job:', deliveryJobId)

    // 1. Verify the delivery_job exists and is in a dispatchable state
    const { data: job, error: jobError } = await supabase
      .from('delivery_jobs')
      .select('id, status, driver_id')
      .eq('id', deliveryJobId)
      .single()

    if (jobError || !job) {
      console.error('[DISPATCH] Delivery job not found:', jobError)
      return {
        success: false,
        offersCreated: 0,
        driversFound: 0,
        error: 'Delivery job not found',
      }
    }

    // Don't dispatch if already assigned
    if (job.status === 'assigned' || job.driver_id) {
      console.log('[DISPATCH] Job already assigned, skipping dispatch')
      return { success: true, offersCreated: 0, driversFound: 0 }
    }

    // Only dispatch for jobs in initial/searching state
    // DB may default to 'open' instead of 'pending'
    const DISPATCHABLE_STATUSES = ['open', 'pending', 'searching']
    if (!DISPATCHABLE_STATUSES.includes(job.status)) {
      console.log('[DISPATCH] Job status is not dispatchable:', job.status)
      return {
        success: false,
        offersCreated: 0,
        driversFound: 0,
        error: `Invalid job status: ${job.status}`,
      }
    }

    // 2. Find available drivers
    const drivers = await findAvailableDrivers(restaurantId)

    if (drivers.length === 0) {
      console.log('[DISPATCH] ⚠️ No available drivers found')
      await addOrderLog(orderId, restaurantId, 'dispatch_no_drivers', {
        delivery_job_id: deliveryJobId,
      })
      return { success: true, offersCreated: 0, driversFound: 0 }
    }

    // 3. Check for existing pending offers to prevent duplicates
    const { data: existingOffers } = await supabase
      .from('delivery_job_offers')
      .select('driver_id')
      .eq('delivery_job_id', deliveryJobId)
      .in('status', ['pending', 'accepted'])

    const alreadyOfferedDriverIds = new Set(
      (existingOffers || []).map((o) => o.driver_id),
    )

    // Filter out drivers who already have an offer
    const newDrivers = drivers.filter(
      (d) => !alreadyOfferedDriverIds.has(d.driver_id),
    )

    if (newDrivers.length === 0) {
      console.log('[DISPATCH] All available drivers already have offers')
      return { success: true, offersCreated: 0, driversFound: drivers.length }
    }

    // 4. Create offers
    const expiresAt = new Date(
      Date.now() + OFFER_EXPIRY_SECONDS * 1000,
    ).toISOString()

    const offersToInsert = newDrivers.map((driver) => ({
      delivery_job_id: deliveryJobId,
      driver_id: driver.driver_id,
      status: 'pending' as const,
      expires_at: expiresAt,
    }))

    const { data: insertedOffers, error: insertError } = await supabase
      .from('delivery_job_offers')
      .insert(offersToInsert)
      .select('id, driver_id')

    if (insertError) {
      console.error('[DISPATCH] Error creating offers:', insertError)
      return {
        success: false,
        offersCreated: 0,
        driversFound: drivers.length,
        error: insertError.message,
      }
    }

    const offersCreated = insertedOffers?.length || 0
    console.log(
      `[DISPATCH] ✅ Created ${offersCreated} offers for job ${deliveryJobId}`,
    )

    // 5. Update delivery_job status to searching if still pending
    await supabase
      .from('delivery_jobs')
      .update({ status: 'searching' })
      .eq('id', deliveryJobId)
      .eq('status', 'pending') // Only update if still pending (optimistic concurrency)

    // 6. Log dispatch
    await addOrderLog(orderId, restaurantId, 'dispatch_offers_created', {
      delivery_job_id: deliveryJobId,
      offers_created: offersCreated,
      drivers_found: drivers.length,
      driver_ids: newDrivers.map((d) => d.driver_id),
      expires_at: expiresAt,
    })

    // 7. Add tracking event for customer
    await addTrackingEvent(
      orderId,
      restaurantId,
      'searching_driver',
      'Buscando conductor',
      `Enviamos tu pedido a ${offersCreated} conductor${offersCreated > 1 ? 'es' : ''} disponible${offersCreated > 1 ? 's' : ''}`,
    )

    console.log('[DISPATCH] ═══════════════════════════════════')
    return { success: true, offersCreated, driversFound: drivers.length }
  } catch (err: any) {
    console.error('[DISPATCH] Exception:', err)
    return {
      success: false,
      offersCreated: 0,
      driversFound: 0,
      error: err.message,
    }
  }
}

// ─── RE-DISPATCH (for expired/rejected offers) ────────────────────

/**
 * Re-dispatches a delivery job that has no remaining pending offers.
 * Called when all offers expire or are rejected.
 * Can be triggered manually from admin or via a periodic check.
 */
export async function redispatchDeliveryJob(
  deliveryJobId: number,
): Promise<DispatchResult> {
  try {
    console.log('[DISPATCH] Re-dispatching job:', deliveryJobId)

    // Fetch the job
    const { data: job, error: jobError } = await supabase
      .from('delivery_jobs')
      .select('id, order_id, restaurant_id, status, driver_id')
      .eq('id', deliveryJobId)
      .single()

    if (jobError || !job) {
      return {
        success: false,
        offersCreated: 0,
        driversFound: 0,
        error: 'Job not found',
      }
    }

    // Don't redispatch if already assigned
    if (job.driver_id || job.status === 'assigned') {
      return { success: true, offersCreated: 0, driversFound: 0 }
    }

    // Reset job status to pending for fresh dispatch
    await supabase
      .from('delivery_jobs')
      .update({ status: 'pending' })
      .eq('id', deliveryJobId)

    // Dispatch again
    return await dispatchDeliveryJob(
      deliveryJobId,
      job.restaurant_id,
      job.order_id,
    )
  } catch (err: any) {
    console.error('[DISPATCH] Exception re-dispatching:', err)
    return {
      success: false,
      offersCreated: 0,
      driversFound: 0,
      error: err.message,
    }
  }
}
